"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@lib/redux/hooks";
import { clearProfile, setProfile } from "@lib/redux/slices/profileSlice";
import { usePersistor } from "@lib/redux/PersistorContext";
import { useRouter, usePathname } from "next/navigation";
import { getClientCookie } from "@lib/cookies.client";
import { useIsMobile } from "@hooks/use-mobile";
import styles from "./LoginButton.module.scss";
import Image from "next/image";
import React from "react";
import Button from "@components/atoms/Button/index";
import Text from "@components/atoms/Text";
import UserProfile from "@components/molecules/UserProfile";
import assetLoader from "@utils/internalImageLoder";
import { getLoginUrl } from "@utils/getLoginUrl";
import ImageResponsiveAssets from "@components/atoms/ImageResponsiveAssets";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { filterSafeProfileData } from "@utils/profileDataFilter";
import { getPageNameFromUrl } from "@utils/pageNameUtils";
import { clearLeadData } from "@utils/leadDataStorage";

interface UserData {
  name: string;
  phone: string;
  completionPercentage: number;
  avatar: string;
  email: string;
}

interface LoginButtonProps {
  authCookie?: any;
  userProfile?: UserData | null;
  cityName?: any;
}

export function LoginButton({
  authCookie,
  userProfile,
  cityName,
}: LoginButtonProps) {
  const [showUserProfile, setShowUserProfile] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const persistor = usePersistor();
  const isMobile = useIsMobile();

  const reduxUserProfile = useAppSelector((state) => state.profile.data);
  const isAuthenticated = useAppSelector(
    (state) => state.profile.isAuthenticated
  );

  const isLoggedIn = Boolean(authCookie || getClientCookie("ackodrive_tokens"));
  const safeProfile = useMemo(
    () => (userProfile ? filterSafeProfileData(userProfile) : null),
    [userProfile],
  );
  const syncedProfileSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoggedIn && safeProfile) {
      const profileSignature = JSON.stringify(safeProfile);
      if (syncedProfileSignatureRef.current !== profileSignature) {
        syncedProfileSignatureRef.current = profileSignature;
        dispatch(setProfile(safeProfile));
      }
    } else if (!isLoggedIn && isAuthenticated) {
      syncedProfileSignatureRef.current = null;
      dispatch(clearProfile());
    }
  }, [isLoggedIn, safeProfile, isAuthenticated, dispatch]);

  const currentUserProfile = reduxUserProfile || userProfile;
  const userName = reduxUserProfile?.name || userProfile?.name || "User";

  const getAvatarInitial = (name: string) => {
    return name?.charAt(0)?.toUpperCase() || "U";
  };

  const userAvatar = getAvatarInitial(userName);

  const handleLogin = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("loginType", "Direct");
      localStorage.setItem("MMV", JSON.stringify({}));
    }
    window.location.href = getLoginUrl(window.location.href);
  };

  const handleLogout = async () => {
    try {
      const basePath = process.env.NEXT_PUBLIC_BASE_TERM
        ? `${process.env.NEXT_PUBLIC_BASE_TERM}`
        : "";
      const logoutUrl = `/${basePath}api/auth/logout?redirect=${encodeURIComponent(
        window.location.pathname
      )}`;

      const response = await fetch(logoutUrl);
      const result = await response.json();

      if (!result.success) {
        console.error("Logout API error:", result.error);
      }

      dispatch(clearProfile());
      await persistor.flush();
      
      clearLeadData();

      window.location.reload()
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleClick = async () => {
    if (showUserProfile) {
      return setShowUserProfile(false);
    }

    await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
      pageType: getPageNameFromUrl(pathname),
      additionalProps: {
        action_type: 'tap_btn',
        action_name: 'tap_btn_my_profile_options',
        city_name: cityName,
      }
    });

    if (isLoggedIn) {
      if (isMobile) {
        setShowUserProfile(true);
      } else {
        if (!showUserProfile) {
          setShowUserProfile(true);
        }
      }
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: getPageNameFromUrl(pathname),
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_my_profile_options",
          city_name: cityName,
        },
      });
    } else {
      handleLogin();
    }
  };

  const handleLogoutFromProfile = () => {
    setShowUserProfile(false);
    handleLogout();
  };

  return (
    <div className={styles.loginButtonContainer}>
      {isLoggedIn ? (
        <>
          <div 
            className={styles.loggedInDesktop} 
            data-profile-trigger
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            <div className={styles.loggedInUserIconDesktop}>
              <Text className={styles.loggedInUserIconText}>{userAvatar}</Text>
            </div>
            {
              showUserProfile ? 
                <Image 
                  src={`${assetLoader()}/icons/Close_Dark.svg`}
                  alt="Close Profile"
                  width={20}
                  height={20}
                  className={`${styles.loggedInIcon} loggedInIcon`}
                />
              :
                <Image 
                  src={`${assetLoader()}/icons/menuIcon.svg`}
                  alt="Open Profile"
                  width={20}
                  height={20}
                  className={`${styles.loggedInIcon} loggedInIcon`}
                />
            }
          </div>
          <div className={styles.loggedInUserIconMobile} onClick={handleClick}>
            <Text className={styles.loggedInUserIconText}>{userAvatar}</Text>
          </div>
        </>
      ) : (
        <>
          {isMobile ? (
            <ImageResponsiveAssets
              filePath="/icons/profile"
              name="guestUserAvatar"
              alt="userIcon"
              width={32}
              height={32}
              priority
              className={styles.loggedOutUserIcon}
              onClick={handleClick}
            />
          ) : (
            <Button
              className={styles.loggedOutCTADesktop}
              onClick={handleClick}
            >
              Login / Signup
            </Button>
          )}
        </>
      )}

      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        userData={currentUserProfile || {
          name: "User",
          phone: "",
          completionPercentage: 0,
          avatar: "U"
        }}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogoutFromProfile}
        cityName={cityName}
      />
    </div>
  );
}
